/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { WatchActionKernel, WatchFromSoCuAction } from 'client-code/action-details';
import {
    ActionGroup,
    ActionKernelType,
    ActionType,
    ModifyOnePassActionKernelV2,
    WatchFromVodActionKernel,
    WatchNowAction,
} from 'client-code/actions';
import { EpisodeInfo, GroupingType, RecordingStatus } from 'client-code/episode-guide';
import { ScreenType } from 'client-code/previews';
import { ViewType } from 'client-code/watch-stickiness';

import { DeviceModel } from '../../../core/device';
import { InteractiveComponents } from '../../../core/enums';
import { ContentType } from '../../../core/enums/ContentType';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import { NdvrInteractor, ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { KeyManager } from '../../../core/navigation';
import { ScreenController } from '../../../core/navigation/ScreenController';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../core/playback/impls';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import type { RootState, TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { TAction, TActionList, TUpcomingOffer, TVodPlayActionStateType } from '../../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import { DateTime, ListTypes, UniqueIdentifier } from '../../../core/utils';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { GridTypes } from '../../../core/utils/GridTypes';
import { NdvrPlaybackType } from '../../../core/videoplayer/enums';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { setGridItemFocusAction } from '../../grid/grid.actions';
import {
    onKeyDownGrid,
    onKeyLeftGrid,
    onKeyRightGrid,
    onKeyUpGrid,
    setGridFocus,
} from '../../grid/grid.reducers';
import { IGridEntry } from '../../grid/gridprops';
import { setActiveInteractiveComponent } from '../../interactivelayer/interactiveLayer.reducers';
import { setListEntryFocus, setListEntryPosition } from '../../list/list.actions';
import {
    onKeyDownList,
    onKeyLeftList,
    onKeyRightList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../list/list.reducers';
import { IListState } from '../../list/listprops';
import { prepareCreateOnePassOverlay } from '../../overlay/createonepassoverlay/createonepassoverlay.reducres';
import { GLOBAL_MOUSE_TRAP, showGoToOverlay } from '../../overlay/gotooverlay/gotooverlay.reducers';
import { prepareModifyOnePassOverlay } from '../../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';
import {
    onKeyLeftOnePassOptionsOverlay,
    onKeyOkDOCOverlay,
    onKeyOkOnePassOptionsOverlay,
    onKeyRightOnePassOptionsOverlay,
    onMouseClickDOCOptionsOverlay,
    onMouseClickOnePassOptionsOverlay,
    prepareDOCOverlay,
} from '../../overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showPCPinOverlayWithProgramRating,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { showStopAndDeleteRecordingOverlay } from '../../overlay/recordingoverlay/recordingoverlay.reducers';
import {
    onKeyOkStorageLimitOverlay,
    onMouseClickStorageLimitOverlay,
} from '../../overlay/storagelimitoverlay/storagelimitoverlay.reducers';
import {
    onKeyOkStreamingOptionsOverlay,
    onMouseClickStreamingOptionsOverlay,
} from '../../overlay/streamingoptionsoverlay/streamingoptionsoverlay.reducers';
import {
    onKeyOkVodPlayErrorOverlay,
    onMouseClickVodPlayErrorOverlay,
} from '../../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import {
    onKeyOkVodResumeStartOverOverlayForWatchAction,
    prepareVodResumeStartOverOverlayForWatchAction,
} from '../../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import {
    onKeyOkVodUpSellOverlay,
    onMouseClickVodUpSellOverlay,
} from '../../overlay/vodupselloverlay/vodupselloverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../overlaydialog/overlaydialog.reducers';
import {
    collectionScreenTransition,
    programScreenTransition,
} from '../../screens/navigation/screensnavigation';
import { showGuideScreen, showPreviousScreen } from '../../screens/screens.reducers';
import { startVideoPlayback } from '../../videoplayer/videoplayer.reducers';
import { notifyVodPlayActionState } from '../../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import { CollectionActions } from '../CollectionActions';
import { CollectionFilters } from '../CollectionFilters';
import { CollectionFilterType } from '../CollectionFilterType';
import {
    setCollectionPageLoading,
    setCollectionScreenCaretVisibleAction,
    setCollectionScreenLastFocusedItemPositionAction,
    setFocusOnCollectionScreenDeleteAction,
    setFocusOnCollectionScreenGoToEpisodeAction,
} from '../collectionscreen.actions';
import {
    fetchAndUpdateCollectionDetails,
    getAllFocusedListByListState,
    getCollectionScreenInfo,
    getCurrentPagePickerStripFromSeasons,
    getFocusedListByListState,
    getPickerStripFromSeasons,
    getSeasonPickerOverlayList,
    getSeasonsDataForSelectedFilter,
    getSelectedFilterFromPosition,
    isExactPageSize,
    logError,
    onKeyDownCollectionScreenOperations,
    onKeyUpCollectionScreenOperations,
    updateBrowseStickiness,
} from '../collectionscreen.reducers';
import { CollectionSortType } from '../CollectionSortType';
import { TCollectionFilterDetails } from '../TCollectionFilterDetails';
import { TCollectionScreen } from '../TCollectionScreen';
import { TListCollectionItem } from '../TListCollectionItem';

const TAG: string = 'CollectionScreenNavigation';

const FIRST_PAGE_MAXIMUM_POSITION = 8;
const MAXIMUM_POSITION = 9;
const SEASON_OR_YEAR_JUMP_OFFSET = 3;

// Helper function to get and validate grid state
const getValidatedGrid = (grid: IGridEntry | undefined) => {
    if (!grid || (grid.items as any as [])?.length === 0) {
        return null;
    }
    return grid;
};

// Helper function to determine cast grid navigation action
const getCastGridNavigationAction = (
    grid: IGridEntry,
    direction: 'up' | 'down' | 'left' | 'right',
): { shouldHandle: boolean; action?: string } => {
    const { focusedItem } = grid;
    const { row: currentRow, column: currentColumn } = focusedItem;

    switch (direction) {
        case 'up':
            if (grid.isActive) {
                const prevRow = currentRow - 1;
                if (prevRow < 0) return { shouldHandle: false };
                return { shouldHandle: true, action: 'gridUp' };
            }
            break;
        case 'down':
            if (grid.isActive) {
                const nextRow = currentRow + 1;
                const maxRows = Math.floor((grid.items as any as [])?.length / grid.columnLimit);
                if (nextRow >= maxRows) return { shouldHandle: false };
                return { shouldHandle: true, action: 'gridDown' };
            }
            break;
        case 'left':
            if (!grid.isActive) return { shouldHandle: false };
            if (currentColumn === 0) {
                return { shouldHandle: true, action: 'focusFilters' };
            }
            return { shouldHandle: true, action: 'gridLeft' };
        case 'right':
            if (!grid.isActive) {
                return { shouldHandle: true, action: 'focusGrid' };
            }
            if (currentColumn + 1 >= grid.columnLimit) return { shouldHandle: false };
            return { shouldHandle: true, action: 'gridRight' };
        default:
            return { shouldHandle: false };
    }
    return { shouldHandle: false };
};

// Helper function to check if current filter is CAST_DETAIL and get navigation action
const getCastDetailNavigationAction = (
    selectedFilter: string,
    grid: IGridEntry | undefined,
    direction: 'up' | 'down' | 'left' | 'right',
): { shouldHandle: boolean; action?: string } => {
    if (selectedFilter === CollectionFilters.CAST_DETAIL) {
        const validatedGrid = getValidatedGrid(grid);
        if (!validatedGrid) return { shouldHandle: false };
        return getCastGridNavigationAction(validatedGrid, direction);
    }
    return { shouldHandle: false };
};

// Helper function to parse grid position from value string
// Example: 'cast-grid_1_2' returns {row: 1, column: 2}
const parseGridPosition = (value: string): { row: number; column: number } | null => {
    const gridItems = value.split('_');
    if (gridItems.length >= 3) {
        const row = Number(gridItems[1]);
        const column = Number(gridItems[2]);
        if (!Number.isNaN(row) && !Number.isNaN(column)) {
            return { row, column };
        }
    }
    return null;
};

const clearCollectionSharedPreference = () => {
    const collectionActionsSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.COLLECTION_ONE_PASS_ACTION_PREF_KEY,
    ).edit();
    collectionActionsSharedPreferenceEditor.clear();
    collectionActionsSharedPreferenceEditor.commit();
    const collectionFiltersSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.COLLECTION_FILTERS_PREF_KEY,
    ).edit();
    collectionFiltersSharedPreferenceEditor.clear();
    collectionFiltersSharedPreferenceEditor.commit();
    const collectionInfoSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.COLLECTION_INFO_PREF_KEY,
    ).edit();
    collectionInfoSharedPreferenceEditor.clear();
    collectionInfoSharedPreferenceEditor.commit();
};

const setCollectionScreenProps = () => (dispatch: Function, getState: Function) => {
    const { collectionScreen, list } = getState();
    const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const itemPosition =
        list[ListTypes.COLLECTION_ITEMS]?.position ||
        list[ListTypes.COLLECTION_MORE_INFO]?.position ||
        0;

    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    const { offset } = collectionScreen.filters[selectedFilter];

    const collectionScreenProps =
        ScreenController.getInstance().getScreen()?.getProperties() || ({} as TCollectionScreen);
    ScreenController.getInstance()
        .getScreen()
        ?.setProperties({
            ...collectionScreenProps,
            lastOffset: offset,
            lastFilterPosition: filterPosition,
            lastItemPosition: itemPosition,
        });
};

const startPlaybackAfterPCPinVerified = () => async (dispatch: Function, getState: Function) => {
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();
    const ndvrInteractor = new NdvrInteractor();

    try {
        const watchActionsRaw = DeviceModel.getSharedPreferences(
            SharedPrefConstants.COLLECTION_WATCH_ACTIONS,
        ).getString(SharedPrefConstants.COLLECTION_WATCH_ACTIONS);

        KeyManager.getInstance().startKeyIgnoring();

        const watchActions = JSON.parse(watchActionsRaw || '[]');
        const watchNowAction = watchActions?.find(
            (item) => item.actionType === ActionType.WatchNowAction,
        );

        if (!watchNowAction) {
            Log.e(TAG, 'No valid watchNowAction found.');
            KeyManager.getInstance().stopKeyIgnoring();
            return;
        }
        playbackModelImpl.setActionListData(watchActions as TActionList);

        const { collectionScreen, list } = getState() as RootState;
        const position = list[ListTypes.COLLECTION_ITEMS]?.position;
        const filterPosition = list[ListTypes.COLLECTION_FILTERS]?.position;
        const selectedFilter = getSelectedFilterFromPosition(filterPosition);
        const selectedFilterList = collectionScreen.filters[selectedFilter] || {};

        const filterMap: Record<string, string> = {
            [CollectionFilters.ALL_EPISODE_DETAIL]: CollectionFilterType.ALL,
            [CollectionFilters.RECORDINGS_DETAILS]: CollectionFilterType.RECORDINGS,
            [CollectionFilters.WATCH_LIST_DETAILS]: CollectionFilterType.WATCHLIST,
            [CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL]:
                CollectionFilterType.ON_DEMAND,
        };

        const viewTypeMap: Record<string, ViewType> = {
            [CollectionFilters.ALL_EPISODE_DETAIL]: ViewType.All,
            [CollectionFilters.RECORDINGS_DETAILS]: ViewType.Recordings,
            [CollectionFilters.WATCH_LIST_DETAILS]: ViewType.MyShows,
        };

        const viewType = viewTypeMap[selectedFilter] ?? ViewType.Available;
        const filterType = filterMap[selectedFilter] ?? CollectionFilterType.AVAILABLE;

        // Handle Recorded Playback
        if (
            (watchNowAction.kernel.actionKernelType ===
                ActionKernelType.ResumeFromMyShowsActionKernel ||
                watchNowAction.kernel.actionKernelType ===
                    ActionKernelType.WatchFromMyShowsActionKernel) &&
            watchNowAction.kernel?.recordingId
        ) {
            const playbackContentType = watchNowAction.kernel?.isRecordingInProgress
                ? PlaybackContentType.NDVR_LIVE
                : PlaybackContentType.NDVR_RECORDED;

            playbackModelImpl.setPlaybackExtraParams({
                viewType,
                filter: filterType,
                sort: (selectedFilterList.sortOrder || CollectionSortType.BY_SEASON) as string,
            });
            playbackModelImpl.setPlaybackData(playbackContentType);
            dispatch(
                startVideoPlayback({
                    playbackContentType,
                    ndvrPlaybackModel: {
                        id:
                            (selectedFilterList?.episodes?.[position] as EpisodeInfo)
                                ?.recordingId || '',
                        ndvrType: NdvrPlaybackType.RECORDING,
                    },
                }),
            );

            Log.d(TAG, 'Playing Recording:', playbackContentType);
            return;
        }

        // Handle Live Playback
        if (watchNowAction.kernel.actionKernelType === ActionKernelType.WatchLiveActionKernel) {
            try {
                const channel = await ndvrInteractor.getChannelFromChannelNumber(
                    watchNowAction.kernel?.channelNumber,
                );
                playbackModelImpl.setPlaybackData(PlaybackContentType.LIVE);
                dispatch(
                    startVideoPlayback({
                        channel,
                        playbackContentType: PlaybackContentType.LIVE,
                    }),
                );

                Log.d(TAG, 'Playing Live:', watchNowAction.isLive, 'Channel:', channel);
            } catch (error: any) {
                Log.e(TAG, 'Failed to fetch channel for live playback:', error);
                logError(TAG, error, false);
                KeyManager.getInstance().stopKeyIgnoring();
            }
            return;
        }

        // Handle SoCu Playback when actionKernelType is watchFromVodActionKernel
        /** update:
         *  now since we have full mechanism to handle vod playback with watchFromVodActionKernel
         * we can directly call vod playback from here instead of going to streaming options overlay
         * streaming options overlay will be shown only if
         * actionKernelType === streamingOptionsActionKernel
         */
        if (
            watchNowAction.kernel.actionKernelType.toLocaleLowerCase() ===
            ActionKernelType.WatchFromVodActionKernel.toLocaleLowerCase()
        ) {
            try {
                await playbackModelImpl.fetchOttContentActionDetails(
                    watchNowAction.kernel?.contentId,
                    ScreenType.SeriesScreen,
                );

                playbackModelImpl.setPlaybackExtraParams({
                    viewType,
                    filter: filterType,
                    sort: (selectedFilterList.sortOrder || CollectionSortType.BY_SEASON) as string,
                });

                const kernel = watchNowAction.kernel as WatchFromVodActionKernel;

                dispatch(
                    notifyVodPlayActionState({
                        type: TVodPlayActionStateType.INIT,
                        vodRequiredParams: {
                            itemType: 'content',
                            id: kernel.contentId,
                            partnerId: kernel.partnerId,
                            collapsedVodItemId: kernel.collapsedVodItemId || '',
                            contentSupplierPartnerId: kernel.contentSupplierPartnerId,
                        },
                    }),
                );
                Log.d(TAG, 'Playing SoCu playback');
            } catch (error: any) {
                Log.e(TAG, 'Failed to fetch OTT content or channel for SoCu playback:', error);
                logError(TAG, error, false);
                KeyManager.getInstance().stopKeyIgnoring();
            }
        }

        // Handle SoCu Playback when actionKernelType is watchFromSoCuActionKernel
        if (
            (watchNowAction.kernel as WatchActionKernel).actionKernelType.toLocaleLowerCase() ===
            ActionKernelType.WatchFromSocuActionKernel.toLocaleLowerCase()
        ) {
            playbackModelImpl.setPlaybackExtraParams({
                viewType,
                filter: filterType,
                sort: (selectedFilterList.sortOrder || CollectionSortType.BY_SEASON) as string,
            });
            dispatch(handleSoCuPlaybackInSeriesScreen(watchNowAction));
        }
    } catch (error) {
        Log.e(TAG, 'Error in startPlaybackAfterPCPinVerified:', error);
        logError(TAG, error, false);
        KeyManager.getInstance().stopKeyIgnoring();
    }
};

const handleSoCuPlaybackInSeriesScreen = (action: any) => async (dispatch: Function) => {
    const { isCatchUp, isStartOver, socuOfferId: offerId } = action.kernel;
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();
    const actionDetailsList = await playbackModelImpl.fetchActionDetails({
        isCatchUp: !!isCatchUp,
        isStartOver: !!isStartOver,
        screenType: 'collectionScreen',
        offerId,
    });
    const watchAction: WatchFromSoCuAction = actionDetailsList.actionDetails.watchActionsList[0];
    dispatch(prepareVodResumeStartOverOverlayForWatchAction(watchAction, isStartOver));
};

const handlePlayback = () => (dispatch: Function, getState: Function) => {
    const { collectionScreen, list } = getState() as RootState;
    const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    const episode =
        collectionScreen.filters[selectedFilter].episodes[
            list[ListTypes.COLLECTION_ITEMS].position
        ];
    const { isAdult } = episode;

    const collectionInfoSharedPreference = new SharedPreferences(
        SharedPrefConstants.COLLECTION_PREVIEW,
    );
    const collectionPreview = collectionInfoSharedPreference.getJson(
        SharedPrefConstants.COLLECTION_PREVIEW,
    ) as any;

    if (
        CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_VOD_SLT_REMOVE_IN_KST_12207,
        ) &&
        selectedFilter === CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL
    ) {
        const watchActionsRaw = DeviceModel.getSharedPreferences(
            SharedPrefConstants.COLLECTION_WATCH_ACTIONS,
        ).getString(SharedPrefConstants.COLLECTION_WATCH_ACTIONS);
        const watchActions = JSON.parse(watchActionsRaw || '[]');
        const watchNowAction = watchActions?.find(
            (item) => item.actionType === ActionType.WatchNowAction,
        );

        if (!watchNowAction) Log.e(TAG, 'No valid watchNowAction found.');

        if (watchNowAction.kernel.actionKernelType === ActionKernelType.WatchFromVodActionKernel)
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.INIT,
                    vodRequiredParams: {
                        itemType: 'content',
                        id: watchNowAction.kernel.contentId,
                        partnerId: watchNowAction.kernel.partnerId,
                        collapsedVodItemId: watchNowAction.kernel.collapsedVodItemId,
                    },
                }),
            );

        return;
    }

    try {
        const { previewType, internalRatingList } = collectionPreview;
        const servicesClient = new ServicesClientImpl();

        const isViolatedRating = servicesClient.checkPCViolationsWithRatings(
            internalRatingList || [],
            !!isAdult,
            previewType,
        );
        const rating = servicesClient.getRestrictedRatingName(internalRatingList || []);

        if (isViolatedRating) {
            Log.d(TAG, 'Playback restricted due to rating violation:', rating);
            dispatch(
                showPCPinOverlayWithProgramRating(
                    startPlaybackAfterPCPinVerified,
                    !!isAdult,
                    rating,
                ),
            );
        } else {
            Log.d(TAG, 'No rating violation detected, proceeding with playback.');
            dispatch(startPlaybackAfterPCPinVerified());
        }
    } catch (error) {
        Log.e(TAG, 'Error in handlePlayback:', error);
    }
};

const getHighlightInfoForTemporaryDelete = (
    position: number,
    offset: number,
    episodeList: TListCollectionItem[],
    isSeasonPickerNavigable: boolean,
) => {
    /*
     *  In case of the last element of the list the highlight should move above else below.
     *
     *  Here ideally position, episodeList.length should be absolutePositon, totalCount,
     *  but this is not possible as season picker is also considered item in our logic.
     *
     *  Current condition also works as we delete element relatively and never reach 9, 10
     *  index in 1st page and so on pages respectively
     */
    let highlightShiftDirection: 'up' | 'down';
    let updatedPosition: number;

    if (position === episodeList.length - 1) {
        highlightShiftDirection = 'up';
        updatedPosition = position - 1;
    } else {
        highlightShiftDirection = 'down';
        updatedPosition = position + 1;
    }

    if (updatedPosition > 0) {
        const isUpdatedPositionSeasonPicker = episodeList[updatedPosition].contentId === '';

        if (isUpdatedPositionSeasonPicker && !isSeasonPickerNavigable) {
            if (highlightShiftDirection === 'up') updatedPosition -= 1;
            else updatedPosition += 1;
        }
    }

    // For last item in the page highlight goes to next page
    const willHighlightShiftToNextPage =
        (offset === 0 && position === FIRST_PAGE_MAXIMUM_POSITION) ||
        (offset !== 0 && position === MAXIMUM_POSITION);

    return { updatedPosition, willHighlightShiftToNextPage };
};

/*
 *  Adjusts the highlight position considering all edge cases for item list
 *  In case empty fallback to filters list
 */
const safeHighlightForItemsList = () => (dispatch: Function, getState: TGetState) => {
    const { collectionScreen, list } = getState();

    const { item, selectedFilter, position } = getSelectedItem(
        list,
        collectionScreen.filters,
        ListTypes.COLLECTION_ITEMS,
    );

    // empty
    if (collectionScreen.filters[selectedFilter].episodes.length === 0) {
        dispatch(setFocusOnCollectionScreenDeleteAction(selectedFilter, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
        return;
    }

    // out of bound
    if (position > collectionScreen.filters[selectedFilter].episodes.length - 1) {
        dispatch(
            setListEntryPosition(
                ListTypes.COLLECTION_ITEMS,
                collectionScreen.filters[selectedFilter].episodes.length - 1,
            ),
        );
        return;
    }

    const isNavigableSeasonPicker = !!list[ListTypes.COLLECTION_SEASON_PICKER];
    // is non navigable season picker
    if (item.contentId === '' && !isNavigableSeasonPicker) {
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, true));
        // this will always work as season picker can't be the last element of the list
        dispatch(setListEntryPosition(ListTypes.COLLECTION_SEASON_PICKER, position + 1));
    }
};

export const onKeyBackCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_BROWSE_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(hideOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            dispatch(onKeyBackCollectionScreenHandler());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        default:
    }
};

export const onKeyBackCollectionScreenHandler = () => (dispatch: Function) => {
    UniqueIdentifier.getNextOnePassServiceNetworkCallId();
    dispatch(updateBrowseStickiness());
    clearCollectionSharedPreference();

    if (NetworkManager.isNetworkDisconnected()) dispatch(showGuideScreen());
    else dispatch(showPreviousScreen());
};

export const onKeyChannelDownCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { collectionScreen, screens, list } = getState();
    const { screenTaskController } = screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            if (
                list[ListTypes.COLLECTION_ITEMS]?.isFocused ||
                list[ListTypes.COLLECTION_SEASON_PICKER]?.isFocused
            ) {
                const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
                const selectedFilter = getSelectedFilterFromPosition(filterPosition);
                const { offset } = collectionScreen.filters[selectedFilter];
                if (offset === 0)
                    dispatch(onKeyDownCollectionScreenOperations(FIRST_PAGE_MAXIMUM_POSITION));
                else dispatch(onKeyDownCollectionScreenOperations(MAXIMUM_POSITION));
            }
            break;
        default:
    }
};

export const onKeyChannelUpCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screens, collectionScreen, list } = getState();
    const { position = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(position);
    const { screenTaskController } = screens;
    const { offset } = collectionScreen.filters[selectedFilter];

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            if (
                offset !== 0 &&
                (list[ListTypes.COLLECTION_ITEMS]?.isFocused ||
                    list[ListTypes.COLLECTION_SEASON_PICKER]?.isFocused)
            )
                dispatch(onKeyUpCollectionScreenOperations(1));
            break;
        default:
    }
};

export const onKeyDownCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            dispatch(onKeyDownCollectionScreenHandler());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        default:
    }
};

const onKeyDownCollectionScreenHandler = () => (dispatch: Function, getState: Function) => {
    const state = getState();
    const { list } = state;
    const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);

    // Handle cast detail navigation first
    const castAction = getCastDetailNavigationAction(
        selectedFilter,
        state.grid[GridTypes.CAST],
        'down',
    );
    if (castAction.shouldHandle) {
        switch (castAction.action) {
            case 'gridDown':
                dispatch(onKeyDownGrid(GridTypes.CAST));
                break;
            default:
        }
        return;
    }

    const focusedListType = getFocusedListByListState(list);
    if (!focusedListType) return;
    if (
        focusedListType === ListTypes.COLLECTION_ITEMS ||
        focusedListType === ListTypes.COLLECTION_SEASON_PICKER
    ) {
        dispatch(onKeyDownCollectionScreenOperations());
    } else {
        dispatch(setCollectionPageLoading(true));
        dispatch(onKeyDownList(focusedListType));
    }
};

export const onKeyDigitCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;
        default:
    }
};
export const onKeyLeftCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screens, list } = getState();
    const { screenTaskController } = screens;
    const { position: filterPosition } = list[ListTypes.COLLECTION_FILTERS];
    const focusedListType = getFocusedListByListState(list);
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    if (!focusedListType) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            if (focusedListType === ListTypes.COLLECTION_SEASON_PICKER) {
                const { position = 0 } = list[ListTypes.COLLECTION_SEASON_PICKER];
                const { position: itemPosition = 0 } = list[ListTypes.COLLECTION_ITEMS];
                if (position - 1 < 0) {
                    dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, false));
                    dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
                    dispatch(
                        setCollectionScreenLastFocusedItemPositionAction(
                            selectedFilter,
                            itemPosition,
                        ),
                    );
                } else {
                    dispatch(onKeyLeftList(ListTypes.COLLECTION_SEASON_PICKER));
                }
            } else {
                dispatch(onKeyLeftCollectionScreenHandler());
            }
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyLeftOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;

        default:
    }
};

const onKeyLeftCollectionScreenHandler = () => (dispatch: Function, getState: Function) => {
    const state = getState() as RootState;
    const { list, collectionScreen } = state;
    const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);

    // Handle cast detail navigation first
    const castAction = getCastDetailNavigationAction(
        selectedFilter,
        state.grid[GridTypes.CAST],
        'left',
    );
    if (castAction.shouldHandle) {
        switch (castAction.action) {
            case 'focusFilters':
                dispatch(setGridFocus(GridTypes.CAST, false));
                dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
                break;
            case 'gridLeft':
                dispatch(onKeyLeftGrid(GridTypes.CAST));
                break;
            default:
        }
        return;
    }

    if (selectedFilter === CollectionFilters.MORE_INFO_DETAIL) {
        dispatch(setListEntryFocus(ListTypes.COLLECTION_MORE_INFO, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
        // There is only one item in more info list
        dispatch(setCollectionScreenLastFocusedItemPositionAction(selectedFilter, 0));
        return;
    }

    UniqueIdentifier.getNextOnePassServiceNetworkCallId();

    if (
        selectedFilter === CollectionFilters.GET_THIS_SHOW_DETAILS ||
        selectedFilter === CollectionFilters.ONEPASS_OPTIONS_DETAIL
    ) {
        const { position = 0 } = list[ListTypes.COLLECTION_ONEPASS_ITEMS];
        // TODO: Need to find better solution
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ONEPASS_ITEMS, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
        dispatch(setCollectionScreenLastFocusedItemPositionAction(selectedFilter, position));
        return;
    }

    const { isDeleteButtonEnable, isGoToEpisodeButtonEnable } =
        collectionScreen.filters[selectedFilter];

    if (isDeleteButtonEnable) {
        dispatch(setFocusOnCollectionScreenDeleteAction(selectedFilter, false));
        dispatch(setFocusOnCollectionScreenGoToEpisodeAction(selectedFilter, true));
        return;
    }

    if (isGoToEpisodeButtonEnable) {
        dispatch(setFocusOnCollectionScreenGoToEpisodeAction(selectedFilter, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, true));
        return;
    }

    const { position = 0 } = list[ListTypes.COLLECTION_ITEMS];

    // TODO: Need to find better solution
    dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, false));
    dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
    dispatch(setCollectionScreenLastFocusedItemPositionAction(selectedFilter, position));
};

const getSelectedItem = (
    list: IListState,
    collectionScreen: { [filter: string]: TCollectionFilterDetails },
    listType: string,
) => {
    const { position = 0 } = list[listType];
    const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    const { offset } = collectionScreen[selectedFilter];
    const item = collectionScreen[selectedFilter].episodes[position];
    return { item, selectedFilter, position, offset };
};

const handleDeleteAction =
    (skipTemporaryDeleteAction: boolean = false) =>
    async (dispatch: Function, getState: Function) => {
        const state = getState() as RootState;
        const { collectionScreen, list } = state;
        const { item, selectedFilter, position, offset } = getSelectedItem(
            list,
            collectionScreen.filters,
            ListTypes.COLLECTION_ITEMS,
        );
        if ('recordingId' in item) {
            const services = new NdvrInteractor();

            const { updatedPosition, willHighlightShiftToNextPage } =
                getHighlightInfoForTemporaryDelete(
                    position,
                    offset,
                    collectionScreen.filters[selectedFilter].episodes,
                    !!list[ListTypes.COLLECTION_SEASON_PICKER], // if this exists then we can navigate
                );

            try {
                // If willHighlightShiftToNextPage no need to notify temporary delete
                if (!skipTemporaryDeleteAction && !willHighlightShiftToNextPage) {
                    UIOrchestrator.stateChangeListener.notify(
                        UIOrchestratorState.RECORDING_TEMP_DELETED,
                    );
                }

                if (updatedPosition >= 0 && !skipTemporaryDeleteAction)
                    await dispatch(fetchAndUpdateCollectionDetails(true, offset, updatedPosition));

                await services.getRequestModelToDeleteRecordingSchedule(item.recordingId!).send();

                services.clearSeasonsData();
                services.clearCachedOffers();

                // fetch updated list
                await dispatch(fetchAndUpdateCollectionDetails(true, offset, position));
                dispatch(safeHighlightForItemsList()); // handles all edge cases

                UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.RECORDING_DELETED);
            } catch (error) {
                Log.e(TAG, 'Error deleting item or updating collection details:', error);
                logError(TAG, error, false);

                if (!skipTemporaryDeleteAction) {
                    if (!willHighlightShiftToNextPage)
                        UIOrchestrator.stateChangeListener.notify(
                            UIOrchestratorState.RECORDING_DELETION_FAILED,
                        );

                    dispatch(fetchAndUpdateCollectionDetails(true, offset, position));
                }
                dispatch(
                    showWhisper({
                        message: Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE),
                        iconType: WhisperTypes.NO_ICON,
                    }),
                );
            } finally {
                dispatch(hideOverlayDialog());
            }
        } else {
            Log.w(TAG, 'Item does not have a recordingId, skipping delete action.');
        }
    };

export const handleStopRecording = () => async (dispatch: Function, getState: TGetState) => {
    const state = getState();
    const { overlayHighlight, overlayDialog } = state;
    const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[overlayHighlightVerticalPosition];
    const globalMousetrapRecordingOverlay = 'HideRecordingOverlay';

    if (
        selectedOption.text === Locale.getString(LocaleKeys.OVERLAY_OPTION_CANCEL_TEXT) ||
        state.inputFactory.value === globalMousetrapRecordingOverlay
    )
        dispatch(hideOverlayDialog());
    else await dispatch(handleDeleteAction(true));
};

export const onKeyOkCollectionScreen = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const { screenTaskController } = state.screens;

    const { list } = getState() as RootState;
    const filterPosition = list[ListTypes.COLLECTION_FILTERS]?.position;
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    const viewTypeMap: Record<string, ViewType> = {
        [CollectionFilters.ALL_EPISODE_DETAIL]: ViewType.All,
        [CollectionFilters.RECORDINGS_DETAILS]: ViewType.Recordings,
        [CollectionFilters.WATCH_LIST_DETAILS]: ViewType.MyShows,
    };
    const viewType = viewTypeMap[selectedFilter] ?? undefined;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkDOCOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            dispatch(onKeyOkCollectionScreenHandler());
            break;
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
            dispatch(onKeyOkOnCollectionScreenGoToOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyOkOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onKeyOkStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(handleStopRecording());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
            dispatch(onKeyOkStreamingOptionsOverlay({ viewType }));
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyOkVodResumeStartOverOverlayForWatchAction());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onKeyOkVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyOkVodUpSellOverlay());
            break;
        default:
    }
};

const deleteRecordingHandler = () => async (dispatch: Function, getState: Function) => {
    const state = getState() as RootState;
    const { collectionScreen, list } = state;
    const { item } = getSelectedItem(
        list,
        collectionScreen.filters,
        ListTypes.COLLECTION_ITEMS,
    ) as any;
    if (item.recordingStatus === RecordingStatus.InProgressRecording) {
        dispatch(
            showStopAndDeleteRecordingOverlay(
                Locale.getString(
                    LocaleKeys[
                        new SharedPreferences(
                            SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
                        ).getString(SharedPrefConstants.RECORDING_ACTION_KERNEL_TYPE)
                    ],
                ),
            ),
        );
    } else {
        await dispatch(handleDeleteAction());
    }
};

export const deleteButtonAction = () => async (dispatch: Function, getState: Function) => {
    const state = getState() as RootState;
    const { collectionScreen, list } = state;
    const { item, selectedFilter, position, offset } = getSelectedItem(
        list,
        collectionScreen.filters,
        ListTypes.COLLECTION_ITEMS,
    );
    const services = new NdvrInteractor();
    switch (selectedFilter) {
        case CollectionFilters.RECORDINGS_DETAILS:
            dispatch(deleteRecordingHandler());
            break;
        case CollectionFilters.WATCH_LIST_DETAILS:
            if ('recordingId' in item) {
                dispatch(deleteRecordingHandler());
            } else if ('contentId' in item) {
                const { updatedPosition, willHighlightShiftToNextPage } =
                    getHighlightInfoForTemporaryDelete(
                        position,
                        offset,
                        collectionScreen.filters[selectedFilter].episodes,
                        !!list[ListTypes.COLLECTION_SEASON_PICKER],
                    );

                try {
                    // If willHighlightShiftToNextPage no need to notify temporary delete
                    if (!willHighlightShiftToNextPage)
                        UIOrchestrator.stateChangeListener.notify(
                            UIOrchestratorState.BOOKMARK_TEMP_DELETED,
                        );

                    if (updatedPosition >= 0)
                        await dispatch(
                            fetchAndUpdateCollectionDetails(true, offset, updatedPosition),
                        );

                    await services.getRequestModelToDeleteBookmarkSchedule(item.contentId).send();

                    services.clearSeasonsData();
                    services.clearCachedOffers();

                    // fetch updated list
                    await dispatch(fetchAndUpdateCollectionDetails(true, offset, position));
                    dispatch(safeHighlightForItemsList()); // handles all edge cases

                    UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.BOOKMARK_DELETED);
                } catch (error: any) {
                    Log.e(TAG, 'Error deleting item or updating collection details:', error);
                    logError(TAG, error, false);

                    if (!willHighlightShiftToNextPage)
                        UIOrchestrator.stateChangeListener.notify(
                            UIOrchestratorState.BOOKMARK_DELETION_FAILED,
                        );

                    dispatch(fetchAndUpdateCollectionDetails(true, offset, position));
                    dispatch(
                        showWhisper({
                            message: Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE),
                            iconType: WhisperTypes.NO_ICON,
                        }),
                    );
                }
            }
            break;
        default:
    }
};

const onKeyOkCollectionFiltersHandler = () => (dispatch: Function, getState: Function) => {
    const { list, collectionScreen } = getState();
    const { position = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(position);
    const { episodes } = collectionScreen.filters[selectedFilter];
    if (
        selectedFilter === CollectionFilters.ONEPASS_OPTIONS_DETAIL ||
        selectedFilter === CollectionFilters.GET_THIS_SHOW_DETAILS
    ) {
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ONEPASS_ITEMS, true));
    } else if (selectedFilter === CollectionFilters.MORE_INFO_DETAIL) {
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_MORE_INFO, true));
    } else {
        if (episodes && episodes.length === 0) {
            return;
        }
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, true));
    }
};

const onKeyOkCollectionItemsHandler = () => async (dispatch: Function, getState: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showWhisper());
        return;
    }

    const { collectionScreen, list } = getState() as RootState;
    const { position = 0 } = list[ListTypes.COLLECTION_ITEMS];
    const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    const { isDeleteButtonEnable, isGoToEpisodeButtonEnable } =
        collectionScreen.filters[selectedFilter];
    const ndvrInteractor = new NdvrInteractor();

    const episode = collectionScreen.filters[selectedFilter]?.episodes?.[position];
    let watchNowActions: TAction[];
    try {
        let res: TActionList;
        const collectionWatchActions = new SharedPreferences(
            SharedPrefConstants.COLLECTION_WATCH_ACTIONS,
        );
        KeyManager.getInstance().startKeyIgnoring();
        watchNowActions = JSON.parse(
            collectionWatchActions.getString(SharedPrefConstants.COLLECTION_WATCH_ACTIONS) || '[]',
        );
        if (!(watchNowActions && watchNowActions?.length)) {
            if ((episode as EpisodeInfo)?.recordingId)
                res = await ndvrInteractor.getRecordingActionsV2({
                    recordingId: (episode as EpisodeInfo).recordingId || '',
                    screenType: 'SeriesScreen',
                    groups: [ActionGroup.Watch, ActionGroup.ScheduleRecording],
                    actionsNetworkCallId: UniqueIdentifier.getNextCollectionActionNetworkCallId(),
                });
            else
                res = await ndvrInteractor.getContentActions({
                    contentId: episode?.contentId || '',
                    screenType: 'SeriesScreen',
                    groups: [ActionGroup.Watch],
                    actionsNetworkCallId: UniqueIdentifier.getNextCollectionActionNetworkCallId(),
                });
            const collectionWatchActionsEditor = collectionWatchActions.edit();
            collectionWatchActionsEditor.putString(
                SharedPrefConstants.COLLECTION_WATCH_ACTIONS,
                JSON.stringify(res.actionList.actions),
            );
            collectionWatchActionsEditor.apply();
            watchNowActions = res.actionList.actions;
        }

        const watchNowAction = watchNowActions.find(
            (item) => item.actionType === ActionType.WatchNowAction,
        ) as WatchNowAction | undefined;

        if (isGoToEpisodeButtonEnable || selectedFilter === CollectionFilters.UPCOMING_DETAIL) {
            // TODO: Handle on key ok for Go To Episode button
            Log.i(TAG, 'Go To Episode button pressed', collectionScreen.filters[selectedFilter]);
            dispatch(updateBrowseStickiness());
            dispatch(setCollectionScreenProps());
            if ('recordingStatus' in episode && episode.recordingId) {
                dispatch(
                    programScreenTransition(
                        {
                            id: episode.recordingId,
                            contentType: ContentType.RECORDING_ID,
                        },
                        showWhisper,
                    ),
                );
            } else if (selectedFilter === CollectionFilters.UPCOMING_DETAIL) {
                dispatch(
                    programScreenTransition(
                        {
                            id: (episode as TUpcomingOffer).offerId,
                            contentType: ContentType.OFFER_ID,
                        },
                        showWhisper,
                    ),
                );
            } else {
                const { partnerId, collapsedVodItemId, contentSupplierPartnerId } =
                    getCollectionScreenInfo() || {};
                dispatch(
                    programScreenTransition(
                        {
                            id: episode.contentId,
                            contentType: ContentType.CONTENT_ID,
                            partnerId,
                            collapsedVodItemId,
                            contentSupplierPartnerId,
                        },
                        showWhisper,
                    ),
                );
            }
        } else if (isDeleteButtonEnable) {
            Log.i(TAG, 'Delete button pressed');
            KeyManager.getInstance().stopKeyIgnoring();
            dispatch(deleteButtonAction());
        } else if (selectedFilter !== CollectionFilters.UPCOMING_DETAIL && watchNowAction) {
            dispatch(updateBrowseStickiness());
            dispatch(setCollectionScreenProps());
            dispatch(handlePlayback());
        } else {
            // Taking reference from Streamers
            // - Workaround in CCU R3 : Using static program type as ProgramType.EPISODE
            // - In CCU R4, the program type should come from episodeInfo
            KeyManager.getInstance().stopKeyIgnoring();
            dispatch(
                showWhisper({
                    message: Locale.getString(LocaleKeys.WHISPER_EPISODE_UNAVAILABLE),
                    iconType: WhisperTypes.INFO,
                }),
            );
        }
    } catch (error) {
        Log.e(TAG, 'Error parsing watch actions:', error);
        logError(TAG, error, false);

        KeyManager.getInstance().stopKeyIgnoring();
        dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.WHISPER_EPISODE_UNAVAILABLE),
                iconType: WhisperTypes.INFO,
            }),
        );
    }
};

const onKeyOkCollectionOnePassItemsHandler = () => (dispatch: Function, getState: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showWhisper());
        return;
    }

    const { position = 0 } = getState().list[ListTypes.COLLECTION_ONEPASS_ITEMS];
    const onePassSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
    ).edit();
    const sharedPreferences = new SharedPreferences(
        SharedPrefConstants.COLLECTION_ONE_PASS_ACTION_PREF_KEY,
    );
    const onePassActionList = sharedPreferences.getJson(
        SharedPrefConstants.COLLECTION_ONE_PASS_ACTION_LIST,
    ) as any;

    switch (onePassActionList[position]?.kernel?.actionId) {
        case CollectionActions.CANCEL_ONEPASS_V2:
            onePassSharedPreferenceEditor.putString(
                SharedPrefConstants.SELECTED_ONE_PASS_UCID,
                (onePassActionList[position].kernel as ModifyOnePassActionKernelV2)
                    .unifiedCollectionId,
            );
            onePassSharedPreferenceEditor.apply();
            dispatch(prepareDOCOverlay());
            break;
        case CollectionActions.CREATE_ONEPASS_V2:
            onePassSharedPreferenceEditor.putJson(
                SharedPrefConstants.CREATE_ONE_PASS_ACTION,
                onePassActionList[position] as any,
            );
            onePassSharedPreferenceEditor.apply();
            KeyManager.getInstance().startKeyIgnoring();
            dispatch(prepareCreateOnePassOverlay());
            break;
        case CollectionActions.MODIFY_ONEPASS_V2:
            onePassSharedPreferenceEditor.putJson(
                SharedPrefConstants.MODIFY_ONE_PASS_ACTION,
                onePassActionList[position] as any,
            );
            onePassSharedPreferenceEditor.apply();
            KeyManager.getInstance().startKeyIgnoring();
            dispatch(prepareModifyOnePassOverlay());
            break;
        default:
    }
};

const onKeyOkCollectionMoreInfoItemsHandler = () => (dispatch: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showWhisper());
        return;
    }

    dispatch(updateBrowseStickiness());
    dispatch(setCollectionScreenProps());
    dispatch(
        collectionScreenTransition(
            {
                unifiedCollectionId: getCollectionScreenInfo()?.unifiedCollectionId || '',
                transitionScreen: TransitionScreenType.COLLECTION_SCREEN,
            },
            showWhisper,
        ),
    );
};

const onKeyOkCollectionSeasonPickerHandler =
    (seasonOrYear?: string) => async (dispatch: Function, getState: Function) => {
        if (NetworkManager.isNetworkDisconnected()) {
            dispatch(showWhisper());
            return;
        }

        const { collectionScreen, list } = getState() as RootState;
        const { position = 0 } = list[ListTypes.COLLECTION_ITEMS];
        const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
        const { position: pickerPosition = 0 } = list[ListTypes.COLLECTION_SEASON_PICKER];
        const selectedFilter = getSelectedFilterFromPosition(filterPosition);
        const { episodes, sortOrder } = collectionScreen.filters[selectedFilter];
        const pickerStrip = episodes[position];
        const seasonData = await getSeasonsDataForSelectedFilter(selectedFilter, sortOrder);
        const pickerStripMap = getPickerStripFromSeasons(seasonData);
        const currentPickerStripData = pickerStripMap.get(
            (pickerStrip as EpisodeInfo).seasonOrYear || 0,
        );
        if (!currentPickerStripData) return;
        const selectedSeason =
            seasonOrYear || currentPickerStripData?.stripData[pickerPosition] || '';
        if (/^\d+-\d+$/.test(selectedSeason)) {
            const isSeason = seasonData.grouping === GroupingType.Season || false;
            const startIndex = Number(selectedSeason.split('-')[0]);
            let endIndex = Number(selectedSeason.split('-')[1]);
            endIndex = !isSeason ? new DateTime().getFullYearFromShortYear(endIndex) : endIndex;
            const seasonsOrYears = getSeasonPickerOverlayList(seasonData, startIndex, endIndex);
            dispatch(showGoToOverlay(isSeason, seasonsOrYears));
        } else {
            const derivedSeason = selectedSeason === '+' ? -1 : Number(selectedSeason);
            const selectedPickerStrip = pickerStripMap.get(Number(derivedSeason));
            if (!selectedPickerStrip) return;
            const isFirstSeasonOrYear =
                pickerStripMap.keys().next().value === derivedSeason || false;
            let pickedSeasonOffset = 0;
            let episodePosition = isFirstSeasonOrYear ? 1 : 0;
            if (selectedPickerStrip.stripPosition < FIRST_PAGE_MAXIMUM_POSITION - 1) {
                const currentPickerStrips = getCurrentPagePickerStripFromSeasons(
                    seasonData,
                    pickedSeasonOffset,
                    FIRST_PAGE_MAXIMUM_POSITION,
                );
                episodePosition = currentPickerStrips.findIndex(
                    (item) => item.seasonOrYear === selectedPickerStrip.seasonOrYear,
                );
                episodePosition += selectedPickerStrip.stripPosition + 1;
            } else {
                pickedSeasonOffset = selectedPickerStrip.stripPosition - SEASON_OR_YEAR_JUMP_OFFSET;
                const currentPickerStrips = getCurrentPagePickerStripFromSeasons(
                    seasonData,
                    pickedSeasonOffset,
                    FIRST_PAGE_MAXIMUM_POSITION,
                );
                const stripNewPosition = currentPickerStrips.findIndex(
                    (item) => item.seasonOrYear === selectedPickerStrip.seasonOrYear,
                );
                if (
                    currentPickerStrips.length &&
                    derivedSeason === -1 &&
                    pickedSeasonOffset + MAXIMUM_POSITION >= seasonData.totalEpisodesCount
                ) {
                    episodePosition =
                        MAXIMUM_POSITION -
                        currentPickerStrips[currentPickerStrips.length - 1].episodeCount +
                        currentPickerStrips.length;
                } else episodePosition = SEASON_OR_YEAR_JUMP_OFFSET + stripNewPosition + 1;
            }
            dispatch(fetchAndUpdateCollectionDetails(true, pickedSeasonOffset, episodePosition));
            dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, false));
        }
    };

const onKeyOkCollectionScreenHandler = () => (dispatch: Function, getState: Function) => {
    const { collectionScreen, list } = getState();
    const focusedListType = getFocusedListByListState(list);

    const { position = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(position);
    const { isDeleteButtonEnable, isGoToEpisodeButtonEnable } =
        collectionScreen.filters[selectedFilter];

    if (isGoToEpisodeButtonEnable || isDeleteButtonEnable) {
        dispatch(onKeyOkCollectionItemsHandler());
        return;
    }

    if (selectedFilter === CollectionFilters.CAST_DETAIL) {
        const grid = getValidatedGrid(getState().grid[GridTypes.CAST]);
        if (!grid) return;
        if (grid.isActive) {
            // TODO : navigate to person screen
        } else {
            dispatch(setGridFocus(GridTypes.CAST, true));
            dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
        }
        return;
    }

    switch (focusedListType) {
        case ListTypes.COLLECTION_FILTERS:
            dispatch(onKeyOkCollectionFiltersHandler());
            break;
        case ListTypes.COLLECTION_ITEMS:
            dispatch(onKeyOkCollectionItemsHandler());
            break;
        case ListTypes.COLLECTION_ONEPASS_ITEMS:
            dispatch(onKeyOkCollectionOnePassItemsHandler());
            break;
        case ListTypes.COLLECTION_MORE_INFO:
            dispatch(onKeyOkCollectionMoreInfoItemsHandler());
            break;
        case ListTypes.COLLECTION_SEASON_PICKER:
            dispatch(onKeyOkCollectionSeasonPickerHandler());
            break;
        default:
    }
};

export const onKeyOkOnCollectionScreenGoToOverlay =
    () => (dispatch: Function, getState: Function) => {
        const { overlayDialog, overlayHighlight } = getState();
        const { verticalPosition } = overlayHighlight;
        const { uiProperties } = overlayDialog;
        const { options } = uiProperties;
        const selectedOption = options[verticalPosition];
        Log.i(TAG, 'season/year selected: ', selectedOption);
        dispatch(hideOverlayDialog());
        dispatch(onKeyOkCollectionSeasonPickerHandler(selectedOption?.value || ''));
    };

export const onMouseClickOnCollectionScreenGoToOverlay =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(hideOverlayDialog());
        } else dispatch(onKeyOkOnCollectionScreenGoToOverlay());
    };

export const onKeyRightCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screens, list, collectionScreen } = getState();
    const { screenTaskController } = screens;
    const focusedListType = getFocusedListByListState(list);
    if (!focusedListType) return;
    const { position = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(position);
    const { episodes } = collectionScreen.filters[selectedFilter];

    // Handle cast detail navigation first
    const castAction = getCastDetailNavigationAction(
        selectedFilter,
        getState().grid[GridTypes.CAST],
        'right',
    );
    if (castAction.shouldHandle) {
        switch (castAction.action) {
            case 'focusGrid':
                dispatch(setGridFocus(GridTypes.CAST, true));
                dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
                break;
            case 'gridRight':
                dispatch(onKeyRightGrid(GridTypes.CAST));
                break;
            default:
        }
        return;
    }

    if (
        selectedFilter !== CollectionFilters.GET_THIS_SHOW_DETAILS &&
        selectedFilter !== CollectionFilters.ONEPASS_OPTIONS_DETAIL &&
        selectedFilter !== CollectionFilters.MORE_INFO_DETAIL &&
        selectedFilter !== CollectionFilters.CAST_DETAIL &&
        !episodes?.length
    ) {
        return;
    }

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            if (focusedListType === ListTypes.COLLECTION_SEASON_PICKER) {
                dispatch(onKeyRightList(ListTypes.COLLECTION_SEASON_PICKER));
            } else {
                dispatch(onKeyRightCollectionScreenHandler());
            }
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyRightOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;

        default:
    }
};

const onKeyRightCollectionScreenHandler = () => (dispatch: Function, getState: Function) => {
    const { list, collectionScreen } = getState() as RootState;
    const { position = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(position);

    if (selectedFilter === CollectionFilters.MORE_INFO_DETAIL) {
        dispatch(setListEntryFocus(ListTypes.COLLECTION_MORE_INFO, true));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
        return;
    }

    if (
        selectedFilter === CollectionFilters.GET_THIS_SHOW_DETAILS ||
        selectedFilter === CollectionFilters.ONEPASS_OPTIONS_DETAIL
    ) {
        // TODO: Need to find better solution
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ONEPASS_ITEMS, true));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
        return;
    }

    const { isFocused: isItemFocused, position: itemPosition } = list[ListTypes.COLLECTION_ITEMS];
    const { episodes, isDeleteButtonEnable, isGoToEpisodeButtonEnable, lastPosition } =
        collectionScreen.filters[selectedFilter];

    if (!isItemFocused) {
        // TODO: Need to find better solution
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, true));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
        if (!episodes[itemPosition]?.contentId) {
            if (lastPosition !== undefined && lastPosition !== -1) {
                dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, false));
                dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, true));
                dispatch(setListEntryPosition(ListTypes.COLLECTION_SEASON_PICKER, 0));
            } else dispatch(setListEntryPosition(ListTypes.COLLECTION_ITEMS, itemPosition + 1));
        }
        return;
    }
    if (isItemFocused && selectedFilter === CollectionFilters.UPCOMING_DETAIL) return;
    if (isItemFocused && !isGoToEpisodeButtonEnable && !isDeleteButtonEnable) {
        dispatch(setFocusOnCollectionScreenGoToEpisodeAction(selectedFilter, true));
        return;
    }
    if (
        isItemFocused &&
        isGoToEpisodeButtonEnable &&
        (selectedFilter === CollectionFilters.ALL_EPISODE_DETAIL ||
            selectedFilter === CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL)
    )
        return;
    if (isItemFocused && isGoToEpisodeButtonEnable && !isDeleteButtonEnable) {
        dispatch(setFocusOnCollectionScreenGoToEpisodeAction(selectedFilter, false));
        dispatch(setFocusOnCollectionScreenDeleteAction(selectedFilter, true));
    }
};

export const onKeyUpCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            dispatch(onKeyUpCollectionScreenHandler());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;

        default:
    }
};

const onKeyUpCollectionScreenHandler = () => (dispatch: Function, getState: Function) => {
    const state = getState();
    const { list } = state;
    const { position: filterPosition = 0 } = list[ListTypes.COLLECTION_FILTERS];
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);

    // Handle cast detail navigation first
    const castAction = getCastDetailNavigationAction(
        selectedFilter,
        state.grid[GridTypes.CAST],
        'up',
    );
    if (castAction.shouldHandle) {
        switch (castAction.action) {
            case 'gridUp':
                dispatch(onKeyUpGrid(GridTypes.CAST));
                break;
            default:
        }
        return;
    }

    const focusedListType = getFocusedListByListState(list);
    if (!focusedListType) return;
    if (
        focusedListType === ListTypes.COLLECTION_ITEMS ||
        focusedListType === ListTypes.COLLECTION_SEASON_PICKER
    ) {
        dispatch(onKeyUpCollectionScreenOperations());
    } else {
        const { position = 0 } = list[ListTypes.COLLECTION_FILTERS];
        if (focusedListType === ListTypes.COLLECTION_FILTERS && position !== 0)
            dispatch(setCollectionPageLoading(true));
        dispatch(onKeyUpList(focusedListType));
    }
};

export const onMouseClickCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const {
        screens,
        inputFactory,
        collectionScreen,
        list: {
            [ListTypes.COLLECTION_FILTERS]: { position: filterPosition },
        },
    } = getState() as RootState;

    const { value } = inputFactory;
    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    const { offset, totalItems } = collectionScreen.filters[selectedFilter];
    const viewTypeMap: Record<string, ViewType> = {
        [CollectionFilters.ALL_EPISODE_DETAIL]: ViewType.All,
        [CollectionFilters.RECORDINGS_DETAILS]: ViewType.Recordings,
        [CollectionFilters.WATCH_LIST_DETAILS]: ViewType.MyShows,
    };
    const viewType = viewTypeMap[selectedFilter] ?? undefined;

    switch (screens.screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            if (
                value.includes('collection-filters') ||
                value.includes('collection-items') ||
                value.includes('collection-season-picker') ||
                value.includes('collection-onepass-items') ||
                value.includes('collection-more-info')
            ) {
                dispatch(onKeyOkCollectionScreenHandler());
            }

            // Handle cast grid click
            if (value.includes('cast-grid')) {
                if (selectedFilter === CollectionFilters.CAST_DETAIL) {
                    const position = parseGridPosition(value);
                    if (position) {
                        const { row, column } = position;
                        // Set focus to cast grid item and trigger OK action
                        dispatch(setGridFocus(GridTypes.CAST, true));
                        dispatch(setGridItemFocusAction(GridTypes.CAST, { row, column }));
                        dispatch(onKeyOkCollectionScreenHandler());
                    }
                }
                return;
            }

            if (!collectionScreen.isCaretVisible || collectionScreen.isLoadingPage) return;

            if (value === InteractiveComponents.NORTH) {
                dispatch(onKeyUpCollectionScreenOperations(1));
                if (offset - FIRST_PAGE_MAXIMUM_POSITION <= 0)
                    dispatch(setCollectionScreenCaretVisibleAction(false));
            } else if (value === InteractiveComponents.SOUTH) {
                // In 1st page, 10 element are there so 2nd last element index will be 8
                // In any non 1st page 11 elements will be there, 2nd last element index will be 9
                if (offset === 0)
                    dispatch(onKeyDownCollectionScreenOperations(FIRST_PAGE_MAXIMUM_POSITION));
                else dispatch(onKeyDownCollectionScreenOperations(MAXIMUM_POSITION));

                // Dispatch "hide" action on the 2nd last page to hide the caret on the last page.
                if (totalItems && offset + 2 * MAXIMUM_POSITION >= totalItems) {
                    dispatch(setCollectionScreenCaretVisibleAction(false));
                }
            }

            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickDOCOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onMouseClickOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onMouseClickStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
            dispatch(onMouseClickOnCollectionScreenGoToOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onMouseClickVodResumeStartOverOverlayWatchAction());
            break;
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
            if (value === InteractiveComponents.NORTH) {
                dispatch(onKeyUpOverlayDialog());
            } else if (value === InteractiveComponents.SOUTH) {
                dispatch(onKeyDownOverlayDialog());
            } else {
                dispatch(onMouseClickStreamingOptionsOverlay({ viewType }));
            }
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(handleStopRecording());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseClickVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseClickVodUpSellOverlay());
            break;
        default:
            break;
    }
};

export const onMouseClickVodResumeStartOverOverlayWatchAction =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value === 'HideVodResumeStartOverOverlay') {
            dispatch(hideOverlayDialog());
            return;
        }

        dispatch(onKeyOkVodResumeStartOverOverlayForWatchAction());
    };

export const onMouseOutHelper = () => (dispatch: Function, getState: TGetState) => {
    const { inputFactory, list } = getState();

    const filterPosition = list[ListTypes.COLLECTION_FILTERS]?.position;

    if (filterPosition === undefined) return;

    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    const { value } = inputFactory;

    if (value.includes('collection-items')) {
        const menuItems = value.split('_');
        if (menuItems.length === 3) {
            const horizontalPositon = menuItems[2];
            if (horizontalPositon === 'goto')
                dispatch(setFocusOnCollectionScreenGoToEpisodeAction(selectedFilter, false));
            else if (horizontalPositon === 'delete')
                dispatch(setFocusOnCollectionScreenDeleteAction(selectedFilter, false));
        } else dispatch(onMouseOutList());
    } else if (
        value.includes('collection-filters') ||
        value.includes('collection-season-picker') ||
        value.includes('collection-onepass-items') ||
        value.includes('collection-more-info')
    ) {
        dispatch(onMouseOutList());
    } else if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
        dispatch(setCollectionScreenCaretVisibleAction(false));
    }
};

export const onMouseOutCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            dispatch(onMouseOutHelper());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;

        default:
    }
};

const mouseOverHelper = () => async (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const state = getState();
    const { collectionScreen, list } = state;

    const filterPosition = list[ListTypes.COLLECTION_FILTERS]?.position;

    if (filterPosition === undefined) return;

    const selectedFilter = getSelectedFilterFromPosition(filterPosition);
    const { offset, totalItems, isExactPageScrolled, sortOrder } =
        collectionScreen.filters[selectedFilter];

    // This is necessary to prevent two lists from being highlighted at the same time.
    const allFocusedList = getAllFocusedListByListState(list);
    allFocusedList.forEach((item) => {
        if (!value.includes(item)) dispatch(setListEntryFocus(item, false));
    });

    if (value.includes('collection-items')) {
        const menuItems = value.split('_');
        if (menuItems.length === 3) {
            const verticalPosition = Number(menuItems[0]);
            const horizontalPositon = menuItems[2];
            const id = menuItems[1];
            dispatch(setListEntryPosition(id, verticalPosition));
            if (horizontalPositon === 'goto')
                dispatch(setFocusOnCollectionScreenGoToEpisodeAction(selectedFilter, true));
            else if (horizontalPositon === 'delete')
                dispatch(setFocusOnCollectionScreenDeleteAction(selectedFilter, true));
        } else dispatch(onMouseOverList());
    } else if (value.includes('cast-grid')) {
        // Handle cast grid mouse hover
        if (selectedFilter === CollectionFilters.CAST_DETAIL) {
            const position = parseGridPosition(value);
            if (position) {
                const { row, column } = position;
                // Set grid focus and position for cast grid
                dispatch(setGridFocus(GridTypes.CAST, true));
                dispatch(setGridItemFocusAction(GridTypes.CAST, { row, column }));
            }
        }
    } else if (value.includes('collection-season-picker')) {
        const horizontalPositon = value.split('_')[2];
        dispatch(setListEntryPosition(ListTypes.COLLECTION_ITEMS, Number(horizontalPositon)));
        dispatch(onMouseOverList());
    } else if (
        value.includes('collection-filters') ||
        value.includes('collection-onepass-items') ||
        value.includes('collection-more-info')
    ) {
        dispatch(onMouseOverList());
    } else if (value === InteractiveComponents.NORTH) {
        dispatch(setActiveInteractiveComponent(value));
        if (offset > 0 || (offset === 0 && isExactPageScrolled))
            dispatch(setCollectionScreenCaretVisibleAction(true));
    } else if (value === InteractiveComponents.SOUTH) {
        dispatch(setActiveInteractiveComponent(value));
        const isExactPage = await isExactPageSize(totalItems, selectedFilter, sortOrder);
        if (
            offset + MAXIMUM_POSITION < totalItems ||
            (offset === 0 && isExactPage && !isExactPageScrolled)
        )
            dispatch(setCollectionScreenCaretVisibleAction(true));
    }
};

export const onMouseOverCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS:
            dispatch(mouseOverHelper());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;

        default:
    }
};

export const onMouseWheelCollectionScreen = (wheelDeltaY: number) => (dispatch: Function) => {
    if (wheelDeltaY > 0) {
        dispatch(onKeyUpCollectionScreen());
    } else if (wheelDeltaY < 0) {
        dispatch(onKeyDownCollectionScreen());
    }
};
